import React from 'react';
import {
  BoltIcon,
  CurrencyDollarIcon,
  CloudArrowDownIcon
} from '@heroicons/react/24/outline';

/**
 * CostBadge - Shows if a validation used cached data vs live query
 *
 * Props:
 * - usedCache: boolean - Whether validation used cached profiling data
 * - profilingField: string - Which profiling field was used (null_count, distinct_count, etc.)
 * - costSavings: number - Estimated cost savings in dollars
 * - size: 'sm' | 'md' | 'lg' - Badge size
 * - showTooltip: boolean - Whether to show detailed tooltip
 */
const CostBadge = ({
  usedCache = false,
  profilingField = null,
  costSavings = 0,
  size = 'sm',
  showTooltip = true
}) => {
  if (!usedCache) {
    // Live query - show cost indicator
    return (
      <div className="inline-flex items-center">
        <span
          className={`
            inline-flex items-center gap-1 px-2 py-1
            rounded-full text-xs font-medium
            bg-blue-100 text-blue-700
            ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}
          `}
          title={showTooltip ? "Live query - executed on warehouse" : undefined}
        >
          <BoltIcon className={`${size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5'}`} />
          <span>Live</span>
        </span>
      </div>
    );
  }

  // Cached result - show badge without cost amount on main badge
  const formattedSavings = costSavings > 0 ? `$${costSavings.toFixed(4)}` : '';
  const fieldLabel = profilingField ? profilingField.replace('_', ' ') : 'cached';

  return (
    <div className="inline-flex items-center">
      <span
        className={`
          inline-flex items-center gap-1 px-2 py-1
          rounded-full text-xs font-medium
          bg-green-100 text-green-700
          ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}
        `}
        title={showTooltip ? `Cached from profiling.${profilingField}${formattedSavings ? ` - Saved ${formattedSavings}` : ''}` : undefined}
      >
        <CloudArrowDownIcon className={`${size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5'}`} />
        <span>Cached</span>
      </span>
    </div>
  );
};

/**
 * CostSavingsSummary - Shows aggregate cost savings for a set of validations
 *
 * Props:
 * - totalValidations: number - Total number of validations
 * - cachedValidations: number - Number that used cache
 * - totalSavings: number - Total cost savings in dollars
 * - size: 'sm' | 'md' | 'lg' - Component size
 */
export const CostSavingsSummary = ({
  totalValidations = 0,
  cachedValidations = 0,
  totalSavings = 0,
  size = 'md'
}) => {
  const cacheHitRate = totalValidations > 0
    ? ((cachedValidations / totalValidations) * 100).toFixed(1)
    : 0;

  if (totalValidations === 0) {
    return null;
  }

  return (
    <div className={`
      bg-gradient-to-r from-green-50 to-emerald-50
      border border-green-200 rounded-lg
      ${size === 'sm' ? 'p-3' : size === 'md' ? 'p-4' : 'p-6'}
    `}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0">
            <CurrencyDollarIcon className={`
              text-green-600
              ${size === 'sm' ? 'h-5 w-5' : size === 'md' ? 'h-6 w-6' : 'h-8 w-8'}
            `} />
          </div>
          <div>
            <h3 className={`
              font-semibold text-green-900
              ${size === 'sm' ? 'text-sm' : size === 'md' ? 'text-base' : 'text-lg'}
            `}>
              Cost Optimization
            </h3>
            <p className={`
              text-green-700
              ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}
            `}>
              {cachedValidations} of {totalValidations} validations used cached data
            </p>
          </div>
        </div>

        <div className="text-right">
          <div className={`
            font-bold text-green-900
            ${size === 'sm' ? 'text-lg' : size === 'md' ? 'text-xl' : 'text-2xl'}
          `}>
            ${totalSavings.toFixed(4)}
          </div>
          <div className={`
            text-green-600
            ${size === 'sm' ? 'text-xs' : 'text-sm'}
          `}>
            saved
          </div>
        </div>
      </div>

      {/* Cache hit rate bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-green-700 mb-1">
          <span>Cache hit rate</span>
          <span className="font-semibold">{cacheHitRate}%</span>
        </div>
        <div className="w-full bg-green-200 rounded-full h-2">
          <div
            className="bg-green-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${cacheHitRate}%` }}
          />
        </div>
      </div>

      {/* Details */}
      <div className={`
        mt-3 pt-3 border-t border-green-200
        grid grid-cols-3 gap-2
        ${size === 'sm' ? 'text-xs' : 'text-sm'}
      `}>
        <div>
          <div className="text-green-600">Total</div>
          <div className="font-semibold text-green-900">{totalValidations}</div>
        </div>
        <div>
          <div className="text-green-600">Cached</div>
          <div className="font-semibold text-green-900">{cachedValidations}</div>
        </div>
        <div>
          <div className="text-green-600">Live</div>
          <div className="font-semibold text-green-900">{totalValidations - cachedValidations}</div>
        </div>
      </div>
    </div>
  );
};

export default CostBadge;
